import { useState, useEffect, useRef, useMemo } from "react";
import VoiceTextarea from "../components/VoiceTextarea";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Plus, Trash2, Sparkles, ShieldCheck, Loader2, CheckCircle2, Upload, FileText,
  X, Save, Link as LinkIcon, Copy, AlertCircle, ChevronRight, Building2, MapPin,
  Users, Landmark, HandCoins, Paperclip, Scroll,
} from "lucide-react";
import api from "../lib/api";
import SignaturePad from "../components/SignaturePad";
import Modal from "../components/Modal";
import StepIndex, { PrevNextBar } from "../components/StepIndex";
import { HIDROVERSE_TC } from "../lib/hidroverseTerms";
import DatePickerInput from "../components/DatePickerInput";
import { useAuth } from "../context/AuthContext";

/*
 * Phase 2 — HidroVerse SA account application (public, industry-standard).
 *
 * Features:
 *   • 8 labelled sections on one scroll surface (no wizard — easier for phone
 *     users to skim the whole form before committing).
 *   • Auto-save on every change: localStorage for instant persistence, plus a
 *     debounced server-side draft under a UUID token → "Save & get resume link"
 *     button turns that token into a copy-paste URL prospects can revisit from
 *     any device.
 *   • Compliance-doc uploads (company reg, VAT cert, director IDs, bank
 *     confirm, proof of address, BBBEE cert, tax clearance) — stored in R2
 *     under the draft's token so files are owned by the prospect until the
 *     application is submitted.
 *   • T&C modal — verbatim HidroVerse v2025 terms + statutory SA addendum.
 *     Checkbox stays disabled until the user has scrolled to the bottom of
 *     the terms at least once.
 *   • Signature pad for electronic signature; printed name + designation +
 *     place + date collected for the audit trail.
 *
 * Mounted at /apply/account — outside the authenticated Layout.
 */

const ENTITY_TYPES = [
  { v: "company", label: "(Pty) Ltd / Public company" },
  { v: "cc", label: "Close Corporation (CC)" },
  { v: "sole_prop", label: "Sole Proprietor" },
  { v: "partnership", label: "Partnership" },
  { v: "trust", label: "Trust" },
  { v: "other", label: "Other" },
];

const PAYMENT_TERMS = [
  { v: "cod", label: "COD (Cash on Delivery)" },
  { v: "7d", label: "7 days" },
  { v: "14d", label: "14 days" },
  { v: "30d", label: "30 days" },
  { v: "60d", label: "60 days" },
];

const BBBEE_OPTIONS = [
  "Level 1", "Level 2", "Level 3", "Level 4", "Level 5",
  "Level 6", "Level 7", "Level 8", "Non-compliant", "N/A",
];

const DOC_CATEGORIES = [
  { key: "company_reg", label: "Company registration (CIPC / CK1)", required: true },
  { key: "vat_cert", label: "VAT certificate", required: false },
  { key: "director_id", label: "Director / Member / Owner IDs", required: true },
  { key: "bank_confirm", label: "Bank confirmation letter", required: true },
  { key: "proof_of_address", label: "Proof of physical address (≤ 3 months)", required: true },
  { key: "bbbee_cert", label: "BBBEE certificate / affidavit", required: false },
  { key: "tax_clearance", label: "Tax clearance / TCS PIN", required: false },
  { key: "other", label: "Other supporting docs", required: false },
];

const emptyDirector = {
  full_name: "", id_number: "", position: "Director", shareholding_pct: "",
  cell: "", email: "", home_address: "",
  is_surety: true, spouse_consent_required: false,
  id_doc_key: null, id_doc_filename: null,
};
const emptyRef = {
  company: "", contact_name: "", phone: "", email: "",
  monthly_account: "", account_type: "30 days", account_age_months: "",
};

const emptyForm = () => ({
  entity_type: "company",
  trading_name: "",
  trading_as: "",
  registration_number: "",
  vat_number: "",
  industry: "",
  years_in_business: "",
  year_established: "",
  employee_count: "",
  bbbee_level: "",
  website: "",
  contact_name: "",
  contact_designation: "",
  contact_email: "",
  contact_phone: "",
  physical_address: "", physical_city: "", physical_postal_code: "", physical_country: "South Africa",
  postal_same_as_physical: true,
  postal_address: "", postal_city: "", postal_postal_code: "", postal_country: "South Africa",
  delivery_same_as_physical: true,
  delivery_address: "", delivery_city: "", delivery_postal_code: "", delivery_country: "South Africa",
  delivery_contact_name: "", delivery_contact_phone: "",
  bank_name: "", bank_branch: "", bank_branch_code: "",
  bank_account_number: "", bank_account_type: "cheque", bank_account_holder: "",
  trade_references: [ { ...emptyRef }, { ...emptyRef }, { ...emptyRef } ],
  directors: [ { ...emptyDirector } ],
  suretyship_acknowledged: false,
  requested_credit_limit: "",
  requested_payment_terms: "30d",
  expected_monthly_spend: "",
  start_date: "",
  compliance_docs: [],  // { category, r2_key, filename, content_type, size, url }
  terms_accepted: false,
  signed_by_full_name: "",
  signed_by_designation: "",
  signed_at_place: "",
  signed_on_date: new Date().toISOString().slice(0, 10),
  signature_data_url: null,
  notes: "",
});

const LS_KEY_PREFIX = "hv.accountApp";

export default function AccountApplicationPublic() {
  const [params] = useSearchParams();
  const customerId = params.get("customer_id") || null;
  const resumeToken = params.get("resume") || null;

  const [form, setForm] = useState(emptyForm);
  const [token, setToken] = useState(resumeToken || ""); // server-side draft token
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [tcOpen, setTcOpen] = useState(false);
  const [tcReadComplete, setTcReadComplete] = useState(false);
  const [shareUrl, setShareUrl] = useState(""); // populated after server draft save
  const [activeSection, setActiveSection] = useState("company");

  // Close button behaviour: if the user is authenticated (came from internal
  // sidebar / Tools), pop back to the previous page. If they're a public
  // prospect on the /apply/account URL, close the tab if possible, otherwise
  // go to the home page. "Close" is intentional — "Back" would suggest
  // back-a-step-within-the-form, which would confuse users.
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthed = !!user;
  const closeForm = () => {
    if (isAuthed) {
      navigate(-1);
      return;
    }
    // Unauthenticated: try to close the tab. Browsers only allow this for
    // tabs opened by script, so we fall back to navigating home.
    try {
      window.close();
    } catch (_e) { /* noop */ }
    // If the tab is still here a tick later, navigate home as a fallback.
    setTimeout(() => {
      if (!window.closed) window.location.replace("/");
    }, 120);
  };

  // (Previously there was a scroll-based active-section tracker here. In
  // Wave 9 we switched to a step-by-step layout — only the active section is
  // rendered — so the tracker is no longer needed.)

  // ------- Load resume token on mount -------
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (resumeToken) {
        try {
          const { data } = await api.get(`/account-applications/draft/${resumeToken}`);
          if (!cancelled && data?.data) {
            setForm({ ...emptyForm(), ...data.data });
            setToken(resumeToken);
          }
        } catch {
          // fall through to local storage / fresh form
        }
      }
      // Fallback: hydrate from localStorage if present
      if (!cancelled) {
        const lsKey = `${LS_KEY_PREFIX}.${resumeToken || customerId || "anon"}`;
        const cached = localStorage.getItem(lsKey);
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            setForm((f) => ({ ...f, ...parsed }));
          } catch { /* ignore */ }
        }
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------- localStorage autosave (debounced) -------
  useEffect(() => {
    const lsKey = `${LS_KEY_PREFIX}.${resumeToken || customerId || "anon"}`;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(lsKey, JSON.stringify(form));
      } catch { /* quota */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form, resumeToken, customerId]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setD = (idx, patch) =>
    setForm((f) => ({
      ...f,
      directors: f.directors.map((d, i) => (i === idx ? { ...d, ...patch } : d)),
    }));
  const setR = (idx, patch) =>
    setForm((f) => ({
      ...f,
      trade_references: f.trade_references.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));

  // ------- Server-side draft save (manual button) -------
  const saveDraft = async () => {
    setSavingDraft(true);
    setErr("");
    try {
      const { data } = await api.post("/account-applications/draft", {
        draft_token: token || undefined,
        data: form,
      });
      setToken(data.token);
      setLastSaved(data.updated_at);
      const url = `${window.location.origin}/apply/account?resume=${data.token}${customerId ? `&customer_id=${customerId}` : ""}`;
      setShareUrl(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save draft.");
    } finally {
      setSavingDraft(false);
    }
  };

  // ------- Document upload -------
  const uploadDoc = async (category, file) => {
    if (!file) return;
    // Ensure we have a server-side draft token before uploading — docs are
    // keyed by token in R2.
    let t = token;
    if (!t) {
      const { data } = await api.post("/account-applications/draft", { data: form });
      t = data.token;
      setToken(t);
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    const { data } = await api.post(`/account-applications/draft/${t}/upload?category=${encodeURIComponent(category)}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    set({
      compliance_docs: [
        ...form.compliance_docs,
        { category: data.category, r2_key: data.r2_key, filename: data.filename, content_type: data.content_type, size: data.size, url: data.url },
      ],
    });
  };

  const removeDoc = (r2_key) => {
    set({ compliance_docs: form.compliance_docs.filter((d) => d.r2_key !== r2_key) });
  };

  // ------- Validation -------
  const errors = useMemo(() => {
    const e = {};
    if (!form.trading_name.trim()) e.trading_name = "Registered name required";
    if (!form.contact_name.trim()) e.contact_name = "Primary contact required";
    if (!form.contact_email.trim()) e.contact_email = "Email required";
    if (!form.physical_address.trim()) e.physical_address = "Physical address required";
    if (form.directors.length === 0 || !form.directors[0].full_name.trim())
      e.directors = "At least one principal / director required";
    const missingTrade = form.trade_references.filter((r) => r.company.trim()).length < 3;
    if (missingTrade) e.trade_references = "Please provide 3 trade references";
    if (!form.bank_account_number.trim()) e.bank_account_number = "Banking details required";
    if (!form.terms_accepted) e.terms_accepted = "You must accept the Terms & Conditions";
    if (!form.signed_by_full_name.trim()) e.signed_by_full_name = "Signatory full name required";
    if (!form.signature_data_url) e.signature_data_url = "Please provide a signature";
    return e;
  }, [form]);

  const canSubmit = Object.keys(errors).length === 0;

  // ------- Submit -------
  const submit = async () => {
    if (!canSubmit) {
      setErr("Please complete all required fields first.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        draft_token: token || null,
        customer_id: customerId,
        requested_credit_limit: form.requested_credit_limit === "" ? null : Number(form.requested_credit_limit),
        expected_monthly_spend: form.expected_monthly_spend === "" ? null : Number(form.expected_monthly_spend),
        years_in_business: form.years_in_business === "" ? null : Number(form.years_in_business),
        year_established: form.year_established === "" ? null : Number(form.year_established),
        employee_count: form.employee_count === "" ? null : Number(form.employee_count),
        directors: form.directors.map((d) => ({
          ...d,
          shareholding_pct: d.shareholding_pct === "" ? null : Number(d.shareholding_pct),
        })),
        trade_references: form.trade_references.map((r) => ({
          ...r,
          account_age_months: r.account_age_months === "" ? null : Number(r.account_age_months),
        })),
      };
      const { data } = await api.post("/account-applications", payload);
      setDone(data);
      // Clear localStorage cache on success
      const lsKey = `${LS_KEY_PREFIX}.${resumeToken || customerId || "anon"}`;
      localStorage.removeItem(lsKey);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) return <SuccessPanel done={done} />;

  // Section nav (shared StepIndex). Each entry maps to anchor `#aa-section-N`.
  const SECTIONS = [
    { id: "company", icon: Building2, label: "Company details" },
    { id: "addresses", icon: MapPin, label: "Addresses" },
    { id: "principals", icon: Users, label: "Directors / Principals" },
    { id: "banking", icon: Landmark, label: "Banking" },
    { id: "trade-refs", icon: Users, label: "Trade references" },
    { id: "credit", icon: HandCoins, label: "Credit request" },
    { id: "docs", icon: Paperclip, label: "Supporting documents" },
    { id: "terms", icon: Scroll, label: "T&C and signature" },
  ];

  // Per-section completion checks for the StepIndex tick marks.
  const sectionCompleteFn = (key) => {
    if (key === "company") return !!(form.trading_name && form.contact_name && form.contact_email);
    if (key === "addresses") return !!form.physical_address;
    if (key === "principals") return !!(form.directors?.[0]?.full_name);
    if (key === "banking") return !!form.bank_account_number;
    if (key === "trade-refs") return (form.trade_references || []).filter((r) => r.company && r.contact_name).length >= 3;
    if (key === "credit") return !!form.payment_terms || form.requested_credit_limit > 0;
    if (key === "docs") return (form.documents || []).length > 0;
    if (key === "terms") return form.terms_accepted && form.signed_by_full_name && !!form.signature_data_url;
    return false;
  };
  const completedCount = SECTIONS.filter((s) => sectionCompleteFn(s.id)).length;
  const progressByStep = Math.round((completedCount / SECTIONS.length) * 100);

  // Step navigation — jump to a section by state + scroll to top.
  const jumpTo = (key) => {
    setActiveSection(key);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 10);
  };

  // Form completeness for the progress bar — counts "filled" required fields.
  const requiredKeys = [
    "trading_name",
    "contact_name",
    "contact_email",
    "physical_address",
    "bank_account_number",
  ];
  const filled =
    requiredKeys.filter((k) => String(form[k] || "").trim()).length +
    (form.directors?.[0]?.full_name ? 1 : 0) +
    (form.trade_references?.filter((r) => r.company && r.contact_name).length >= 3 ? 1 : 0) +
    (form.terms_accepted ? 1 : 0) +
    (form.signed_by_full_name && form.signature_data_url ? 1 : 0);
  const totalChecks = requiredKeys.length + 4;
  const progress = Math.round((filled / totalChecks) * 100);

  return (
    <div className="min-h-screen bg-[#0a0b0e] text-white" data-testid="account-application-public">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#121419] to-[#0a0b0e] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
          <button
            type="button"
            onClick={closeForm}
            className="hv-btn hv-btn-ghost text-xs mb-3"
            data-testid="aa-close"
            title={isAuthed ? "Close and go back" : "Close this form"}
          >
            <X className="w-4 h-4" /> Close
          </button>
          <div className="text-xs uppercase tracking-[0.28em] text-[#1fb6c1] mb-1">HidroVerse Nelspruit</div>
          <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight">Account Application</h1>
          <p className="text-sm text-[#a0aabf] mt-2 max-w-2xl">
            Open a trade account with HidroVerse. We'll review the information within 3–5 business days. All fields marked <span className="text-rose-300">*</span> are required.
          </p>
          <DraftBar
            token={token}
            shareUrl={shareUrl}
            saving={savingDraft}
            lastSaved={lastSaved}
            onSave={saveDraft}
          />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 grid lg:grid-cols-[260px,1fr] gap-6">
        {/* Table of contents — shared StepIndex (same step-by-step layout as
            Lead Onboarding). Clicking a section jumps to it; only the active
            section is rendered below. */}
        <aside className="self-start">
          <StepIndex
            sections={SECTIONS.map((s) => ({ key: s.id, title: s.label, icon: s.icon }))}
            activeKey={activeSection}
            onJump={jumpTo}
            completionPct={Math.max(progress, progressByStep)}
            isComplete={sectionCompleteFn}
            testId="aa-step-index"
          />
        </aside>

        <div className="space-y-8 min-w-0">
        {err && (
          <div className="border border-rose-500/40 bg-rose-500/5 text-rose-200 text-sm rounded-md p-3 flex items-start gap-2" data-testid="form-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>{err}</div>
          </div>
        )}

        {/* 1. Company details */}
        {activeSection === "company" && (
        <Section id="company" icon={Building2} title="1 · Company details">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Entity type">
              <select className="hv-input" value={form.entity_type} onChange={(e) => set({ entity_type: e.target.value })} data-testid="aa-entity-type">
                {ENTITY_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Industry">
              <input className="hv-input" value={form.industry} onChange={(e) => set({ industry: e.target.value })} placeholder="e.g. Hospitality, Agri, Mining" data-testid="aa-industry" />
            </Field>
            <Field label="Registered name" req error={errors.trading_name}>
              <input className="hv-input" value={form.trading_name} onChange={(e) => set({ trading_name: e.target.value })} data-testid="aa-trading-name" />
            </Field>
            <Field label="Trading as (if different)">
              <input className="hv-input" value={form.trading_as} onChange={(e) => set({ trading_as: e.target.value })} data-testid="aa-trading-as" />
            </Field>
            <Field label="Company / CC registration #">
              <input className="hv-input" value={form.registration_number} onChange={(e) => set({ registration_number: e.target.value })} placeholder="YYYY/######/07" data-testid="aa-reg-number" />
            </Field>
            <Field label="VAT number">
              <input className="hv-input" value={form.vat_number} onChange={(e) => set({ vat_number: e.target.value })} data-testid="aa-vat-number" />
            </Field>
            <Field label="Year established">
              <input className="hv-input" type="number" value={form.year_established} onChange={(e) => set({ year_established: e.target.value })} data-testid="aa-year-established" />
            </Field>
            <Field label="Years in business">
              <input className="hv-input" type="number" value={form.years_in_business} onChange={(e) => set({ years_in_business: e.target.value })} data-testid="aa-years-in-business" />
            </Field>
            <Field label="Employee count">
              <input className="hv-input" type="number" value={form.employee_count} onChange={(e) => set({ employee_count: e.target.value })} data-testid="aa-emp-count" />
            </Field>
            <Field label="BBBEE level">
              <select className="hv-input" value={form.bbbee_level} onChange={(e) => set({ bbbee_level: e.target.value })} data-testid="aa-bbbee">
                <option value="">— Select —</option>
                {BBBEE_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Website" className="sm:col-span-2">
              <input className="hv-input" value={form.website} onChange={(e) => set({ website: e.target.value })} placeholder="https://…" data-testid="aa-website" />
            </Field>
          </div>

          <div className="mt-5 pt-5 border-t border-white/5">
            <div className="text-xs uppercase tracking-wider text-[#647087] mb-3">Primary contact</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Full name" req error={errors.contact_name}>
                <input className="hv-input" value={form.contact_name} onChange={(e) => set({ contact_name: e.target.value })} data-testid="aa-contact-name" />
              </Field>
              <Field label="Designation">
                <input className="hv-input" value={form.contact_designation} onChange={(e) => set({ contact_designation: e.target.value })} placeholder="e.g. Financial Director" data-testid="aa-contact-designation" />
              </Field>
              <Field label="Email" req error={errors.contact_email}>
                <input className="hv-input" type="email" value={form.contact_email} onChange={(e) => set({ contact_email: e.target.value })} data-testid="aa-contact-email" />
              </Field>
              <Field label="Phone">
                <input className="hv-input" type="tel" value={form.contact_phone} onChange={(e) => set({ contact_phone: e.target.value })} data-testid="aa-contact-phone" />
              </Field>
            </div>
          </div>
        </Section>
        )}

        {/* 2. Addresses */}
        {activeSection === "addresses" && (
        <Section id="addresses" icon={MapPin} title="2 · Addresses">
          <div className="text-xs uppercase tracking-wider text-[#647087] mb-2">Physical address</div>
          <AddressBlock prefix="physical" form={form} set={set} errors={errors} />

          <CheckboxRow
            checked={form.postal_same_as_physical}
            onChange={(v) => set({ postal_same_as_physical: v })}
            label="Postal address same as physical"
            testid="aa-postal-same"
          />
          {!form.postal_same_as_physical && (
            <>
              <div className="text-xs uppercase tracking-wider text-[#647087] mb-2 mt-3">Postal address</div>
              <AddressBlock prefix="postal" form={form} set={set} />
            </>
          )}

          <CheckboxRow
            checked={form.delivery_same_as_physical}
            onChange={(v) => set({ delivery_same_as_physical: v })}
            label="Delivery address same as physical"
            testid="aa-delivery-same"
          />
          {!form.delivery_same_as_physical && (
            <>
              <div className="text-xs uppercase tracking-wider text-[#647087] mb-2 mt-3">Delivery address</div>
              <AddressBlock prefix="delivery" form={form} set={set} />
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <Field label="Delivery contact name">
                  <input className="hv-input" value={form.delivery_contact_name} onChange={(e) => set({ delivery_contact_name: e.target.value })} />
                </Field>
                <Field label="Delivery contact phone">
                  <input className="hv-input" type="tel" value={form.delivery_contact_phone} onChange={(e) => set({ delivery_contact_phone: e.target.value })} />
                </Field>
              </div>
            </>
          )}
        </Section>
        )}

        {/* 3. Directors / Principals */}
        {activeSection === "principals" && (
        <Section id="principals" icon={Users} title="3 · Directors / Members / Principals" error={errors.directors}>
          <div className="space-y-4">
            {form.directors.map((d, idx) => (
              <DirectorCard key={idx} idx={idx} data={d} onChange={(patch) => setD(idx, patch)}
                onRemove={form.directors.length > 1 ? () => set({ directors: form.directors.filter((_, i) => i !== idx) }) : null}
                onUploadId={async (file) => {
                  let t = token;
                  if (!t) {
                    const { data } = await api.post("/account-applications/draft", { data: form });
                    t = data.token; setToken(t);
                  }
                  const fd = new FormData(); fd.append("file", file); fd.append("category", "director_id");
                  const { data } = await api.post(`/account-applications/draft/${t}/upload?category=director_id`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                  setD(idx, { id_doc_key: data.r2_key, id_doc_filename: data.filename });
                }} />
            ))}
            <button type="button" className="hv-btn hv-btn-ghost text-xs" onClick={() => set({ directors: [...form.directors, { ...emptyDirector }] })} data-testid="aa-add-director">
              <Plus className="w-3.5 h-3.5" /> Add another principal
            </button>
          </div>
          <CheckboxRow
            checked={form.suretyship_acknowledged}
            onChange={(v) => set({ suretyship_acknowledged: v })}
            label="I acknowledge that principals signing as sureties bind themselves personally, jointly and severally."
            testid="aa-surety"
          />
        </Section>
        )}

        {/* 4. Banking */}
        {activeSection === "banking" && (
        <Section id="banking" icon={Landmark} title="4 · Banking details" error={errors.bank_account_number}>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Bank name"><input className="hv-input" value={form.bank_name} onChange={(e) => set({ bank_name: e.target.value })} data-testid="aa-bank-name" /></Field>
            <Field label="Branch"><input className="hv-input" value={form.bank_branch} onChange={(e) => set({ bank_branch: e.target.value })} /></Field>
            <Field label="Branch code"><input className="hv-input" value={form.bank_branch_code} onChange={(e) => set({ bank_branch_code: e.target.value })} /></Field>
            <Field label="Account type">
              <select className="hv-input" value={form.bank_account_type} onChange={(e) => set({ bank_account_type: e.target.value })}>
                <option value="cheque">Cheque / Current</option>
                <option value="savings">Savings</option>
                <option value="transmission">Transmission</option>
              </select>
            </Field>
            <Field label="Account number" req><input className="hv-input" value={form.bank_account_number} onChange={(e) => set({ bank_account_number: e.target.value })} data-testid="aa-bank-acct" /></Field>
            <Field label="Account holder"><input className="hv-input" value={form.bank_account_holder} onChange={(e) => set({ bank_account_holder: e.target.value })} /></Field>
          </div>
        </Section>
        )}

        {/* 5. Trade references */}
        {activeSection === "trade-refs" && (
        <Section id="trade-refs" icon={Users} title="5 · Trade references (3 required)" error={errors.trade_references}>
          <div className="space-y-4">
            {form.trade_references.map((r, idx) => (
              <TradeRefCard key={idx} idx={idx} data={r} onChange={(patch) => setR(idx, patch)} />
            ))}
          </div>
        </Section>
        )}

        {/* 6. Credit request */}
        {activeSection === "credit" && (
        <Section id="credit" icon={HandCoins} title="6 · Credit request">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Requested credit limit (ZAR)">
              <input className="hv-input" type="number" value={form.requested_credit_limit} onChange={(e) => set({ requested_credit_limit: e.target.value })} placeholder="e.g. 100000" data-testid="aa-credit-limit" />
            </Field>
            <Field label="Preferred payment terms">
              <select className="hv-input" value={form.requested_payment_terms} onChange={(e) => set({ requested_payment_terms: e.target.value })} data-testid="aa-payment-terms">
                {PAYMENT_TERMS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Expected monthly spend (ZAR)">
              <input className="hv-input" type="number" value={form.expected_monthly_spend} onChange={(e) => set({ expected_monthly_spend: e.target.value })} />
            </Field>
            <Field label="When is the account needed?">
              <DatePickerInput value={form.start_date || ""} onChange={(v) => set({ start_date: v })} />
            </Field>
            <Field label="Additional notes" className="sm:col-span-2">
              <VoiceTextarea rows={4} context="account_application_notes" testIdPrefix="account-app-notes-voice" value={form.notes} onChange={(v) => set({ notes: v })} />
            </Field>
          </div>
        </Section>
        )}

        {/* 7. Compliance docs */}
        {activeSection === "docs" && (
        <Section id="docs" icon={Paperclip} title="7 · Supporting documents">
          <div className="text-xs text-[#a0aabf] mb-3">
            Upload each document separately. Accepted formats: PDF, JPG, PNG. Max 20 MB each.
          </div>
          <div className="space-y-3">
            {DOC_CATEGORIES.map((cat) => {
              const docs = form.compliance_docs.filter((d) => d.category === cat.key);
              return <DocCategoryRow key={cat.key} cat={cat} docs={docs} onUpload={(f) => uploadDoc(cat.key, f)} onRemove={removeDoc} />;
            })}
          </div>
        </Section>
        )}

        {/* 8. T&C + signature */}
        {activeSection === "terms" && (
        <Section id="terms" icon={Scroll} title="8 · Terms & Conditions and signature" error={errors.terms_accepted || errors.signed_by_full_name || errors.signature_data_url}>
          <div className="hv-card p-4 bg-white/[0.02]" data-testid="aa-tc-box">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-medium">HidroVerse Terms and Conditions (v2025)</div>
                <div className="text-xs text-[#647087]">Scroll to the bottom before you can accept.</div>
              </div>
              <button type="button" className="hv-btn hv-btn-ghost text-xs" onClick={() => setTcOpen(true)} data-testid="aa-tc-open">
                <Scroll className="w-3.5 h-3.5" /> Read full terms
              </button>
            </div>

            <label className={`flex items-start gap-3 mt-4 p-3 rounded-md border transition ${form.terms_accepted ? "border-emerald-400/40 bg-emerald-400/5" : "border-white/10"}`}>
              <input
                type="checkbox"
                checked={form.terms_accepted}
                disabled={!tcReadComplete}
                onChange={(e) => set({ terms_accepted: e.target.checked })}
                data-testid="aa-tc-accept"
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className={`text-sm ${tcReadComplete ? "" : "text-[#647087]"}`}>
                  {tcReadComplete
                    ? "I have read, understood, and accept the Terms & Conditions including the PoPIA, NCA §103(5) and ECT Act addenda."
                    : "Open the terms above and scroll to the end to unlock this checkbox."}
                </div>
              </div>
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            <Field label="Signatory full name" req error={errors.signed_by_full_name}>
              <input className="hv-input" value={form.signed_by_full_name} onChange={(e) => set({ signed_by_full_name: e.target.value })} data-testid="aa-sig-name" />
            </Field>
            <Field label="Designation / Capacity">
              <input className="hv-input" value={form.signed_by_designation} onChange={(e) => set({ signed_by_designation: e.target.value })} placeholder="e.g. Director duly authorised" data-testid="aa-sig-role" />
            </Field>
            <Field label="Signed at (place)">
              <input className="hv-input" value={form.signed_at_place} onChange={(e) => set({ signed_at_place: e.target.value })} placeholder="e.g. Nelspruit" />
            </Field>
            <Field label="Date">
              <DatePickerInput value={form.signed_on_date || ""} onChange={(v) => set({ signed_on_date: v })} />
            </Field>
          </div>

          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-[#647087] mb-2">Draw your signature</div>
            <div className="max-w-md">
              {form.signature_data_url ? (
                <div className="relative">
                  <img src={form.signature_data_url} alt="signature" className="border border-white/10 rounded-md bg-white w-full" />
                  <button type="button" onClick={() => set({ signature_data_url: null })} className="hv-btn hv-btn-ghost text-xs mt-2" data-testid="aa-sig-clear">
                    <Trash2 className="w-3.5 h-3.5" /> Clear signature
                  </button>
                </div>
              ) : (
                <SignaturePad onSave={(d) => set({ signature_data_url: d })} label="Sign here" testIdPrefix="aa-sig" />
              )}
            </div>
          </div>
        </Section>
        )}

        {/* Step navigation */}
        <PrevNextBar
          sections={SECTIONS.map((s) => ({ key: s.id, title: s.label }))}
          activeKey={activeSection}
          onJump={jumpTo}
          onSubmit={canSubmit ? submit : null}
          submitLabel="Submit application"
          testId="aa-step-nav"
        />

        {/* Submit */}
        <div className="sticky bottom-0 -mx-4 md:-mx-6 px-4 md:px-6 py-4 bg-[#0a0b0e]/95 backdrop-blur border-t border-white/10 z-10">
          <div className="flex items-center justify-between gap-3 flex-wrap max-w-4xl mx-auto">
            <div className="text-xs text-[#647087]">
              {canSubmit ? (
                <span className="text-emerald-300">Ready to submit.</span>
              ) : (
                <span>{Object.keys(errors).length} required field{Object.keys(errors).length === 1 ? "" : "s"} left.</span>
              )}
            </div>
            <button
              type="button"
              onClick={submit}
              disabled={busy || !canSubmit}
              className="hv-btn hv-btn-primary"
              data-testid="aa-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {busy ? "Submitting…" : "Submit application"}
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* T&C modal */}
      {tcOpen && (
        <Modal
          open
          onClose={() => setTcOpen(false)}
          title="HidroVerse — Terms & Conditions"
          testId="aa-tc-modal"
          maxWidth="max-w-3xl"
          footer={
            <div className="flex items-center justify-between gap-2 w-full flex-wrap">
              <div className={`text-xs ${tcReadComplete ? "text-emerald-300" : "text-[#647087]"}`}>
                {tcReadComplete ? "✓ You've read the full terms." : "Scroll to the bottom to accept."}
              </div>
              <button type="button" className="hv-btn hv-btn-primary text-xs" onClick={() => setTcOpen(false)} disabled={!tcReadComplete}>
                Close
              </button>
            </div>
          }
        >
          <TCScroll onReadComplete={() => setTcReadComplete(true)} />
        </Modal>
      )}
    </div>
  );
}

/* ==========================================================================
 * Sub-components
 * ==========================================================================*/

function Section({ id, icon: Icon, title, children, error }) {
  return (
    <section id={id ? `aa-section-${id}` : undefined} className="hv-card p-5 md:p-6 scroll-mt-24" data-section-title={title}>
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/5">
        <div className="w-8 h-8 rounded-md bg-[#1fb6c1]/10 border border-[#1fb6c1]/30 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#1fb6c1]" />
        </div>
        <h2 className="font-display text-lg md:text-xl font-bold">{title}</h2>
        {error && <span className="text-xs text-rose-300 ml-auto"><AlertCircle className="inline w-3.5 h-3.5 mr-1" />{error}</span>}
      </div>
      {children}
    </section>
  );
}

function Field({ label, req, error, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs uppercase tracking-wider text-[#a0aabf]">
        {label}{req ? <span className="text-rose-300"> *</span> : null}
      </span>
      <div className="mt-1">{children}</div>
      {error && <div className="text-xs text-rose-300 mt-1">{error}</div>}
    </label>
  );
}

function CheckboxRow({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-start gap-2 mt-3 text-sm cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="mt-0.5" />
      <span>{label}</span>
    </label>
  );
}

function AddressBlock({ prefix, form, set, errors = {} }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Street address" req={prefix === "physical"} error={errors[`${prefix}_address`]} className="sm:col-span-2">
        <input
          className="hv-input"
          value={form[`${prefix}_address`]}
          onChange={(e) => set({ [`${prefix}_address`]: e.target.value })}
          data-testid={`aa-${prefix}-address`}
        />
      </Field>
      <Field label="City / Town">
        <input className="hv-input" value={form[`${prefix}_city`]} onChange={(e) => set({ [`${prefix}_city`]: e.target.value })} />
      </Field>
      <Field label="Postal code">
        <input className="hv-input" value={form[`${prefix}_postal_code`]} onChange={(e) => set({ [`${prefix}_postal_code`]: e.target.value })} />
      </Field>
      <Field label="Country" className="sm:col-span-2">
        <input className="hv-input" value={form[`${prefix}_country`]} onChange={(e) => set({ [`${prefix}_country`]: e.target.value })} />
      </Field>
    </div>
  );
}

function DirectorCard({ idx, data, onChange, onRemove, onUploadId }) {
  const fileRef = useRef(null);
  return (
    <div className="border border-white/10 rounded-md p-4" data-testid={`aa-director-${idx}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-[#647087]">Principal #{idx + 1}</div>
        {onRemove && (
          <button type="button" onClick={onRemove} className="hv-btn hv-btn-ghost text-xs text-rose-300 hover:text-rose-200">
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Full name" req><input className="hv-input" value={data.full_name} onChange={(e) => onChange({ full_name: e.target.value })} /></Field>
        <Field label="ID / Passport #" req><input className="hv-input" value={data.id_number} onChange={(e) => onChange({ id_number: e.target.value })} /></Field>
        <Field label="Position"><input className="hv-input" value={data.position} onChange={(e) => onChange({ position: e.target.value })} placeholder="Director / Member / Trustee" /></Field>
        <Field label="Shareholding %"><input className="hv-input" type="number" step="0.01" value={data.shareholding_pct} onChange={(e) => onChange({ shareholding_pct: e.target.value })} /></Field>
        <Field label="Cell"><input className="hv-input" type="tel" value={data.cell} onChange={(e) => onChange({ cell: e.target.value })} /></Field>
        <Field label="Email"><input className="hv-input" type="email" value={data.email} onChange={(e) => onChange({ email: e.target.value })} /></Field>
        <Field label="Home address" className="sm:col-span-2"><input className="hv-input" value={data.home_address} onChange={(e) => onChange({ home_address: e.target.value })} /></Field>
      </div>
      <div className="flex flex-wrap gap-4 mt-3 text-xs">
        <CheckboxRow checked={data.is_surety} onChange={(v) => onChange({ is_surety: v })} label="Signs as surety" />
        <CheckboxRow checked={data.spouse_consent_required} onChange={(v) => onChange({ spouse_consent_required: v })} label="Married in COP — spouse consent required" />
      </div>
      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">ID document</div>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onUploadId(e.target.files?.[0])} />
        {data.id_doc_filename ? (
          <div className="flex items-center gap-2 text-xs text-[#a0aabf]">
            <FileText className="w-3.5 h-3.5" /> {data.id_doc_filename}
            <button type="button" onClick={() => onChange({ id_doc_key: null, id_doc_filename: null })} className="text-rose-300 hover:text-rose-200"><X className="w-3 h-3" /></button>
          </div>
        ) : (
          <button type="button" onClick={() => fileRef.current?.click()} className="hv-btn hv-btn-ghost text-xs">
            <Upload className="w-3 h-3" /> Upload ID
          </button>
        )}
      </div>
    </div>
  );
}

function TradeRefCard({ idx, data, onChange }) {
  return (
    <div className="border border-white/10 rounded-md p-4" data-testid={`aa-traderef-${idx}`}>
      <div className="text-xs uppercase tracking-wider text-[#647087] mb-3">Reference #{idx + 1}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Supplier / Company" req><input className="hv-input" value={data.company} onChange={(e) => onChange({ company: e.target.value })} /></Field>
        <Field label="Contact person"><input className="hv-input" value={data.contact_name} onChange={(e) => onChange({ contact_name: e.target.value })} /></Field>
        <Field label="Phone"><input className="hv-input" type="tel" value={data.phone} onChange={(e) => onChange({ phone: e.target.value })} /></Field>
        <Field label="Email"><input className="hv-input" type="email" value={data.email} onChange={(e) => onChange({ email: e.target.value })} /></Field>
        <Field label="Monthly purchases (ZAR)"><input className="hv-input" value={data.monthly_account} onChange={(e) => onChange({ monthly_account: e.target.value })} placeholder="e.g. 50000" /></Field>
        <Field label="Account age (months)"><input className="hv-input" type="number" value={data.account_age_months} onChange={(e) => onChange({ account_age_months: e.target.value })} /></Field>
        <Field label="Account type" className="sm:col-span-2">
          <input className="hv-input" value={data.account_type} onChange={(e) => onChange({ account_type: e.target.value })} placeholder="e.g. 30 days / COD" />
        </Field>
      </div>
    </div>
  );
}

function DocCategoryRow({ cat, docs, onUpload, onRemove }) {
  const fileRef = useRef(null);
  return (
    <div className="border border-white/10 rounded-md p-3" data-testid={`aa-doc-${cat.key}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Paperclip className="w-3.5 h-3.5 text-[#647087]" />
          <div className="text-sm">
            {cat.label}
            {cat.required && <span className="text-rose-300"> *</span>}
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { onUpload(e.target.files?.[0]); if (fileRef.current) fileRef.current.value = ""; }} />
        <button type="button" onClick={() => fileRef.current?.click()} className="hv-btn hv-btn-ghost text-xs">
          <Upload className="w-3 h-3" /> Upload
        </button>
      </div>
      {docs.length > 0 && (
        <div className="mt-2 space-y-1">
          {docs.map((d) => (
            <div key={d.r2_key} className="flex items-center gap-2 text-xs text-[#a0aabf]">
              <FileText className="w-3.5 h-3.5 shrink-0" />
              <a href={d.url} target="_blank" rel="noreferrer" className="truncate hover:text-white underline">{d.filename}</a>
              <span className="text-[#647087] shrink-0">{Math.round((d.size || 0) / 1024)} KB</span>
              <button type="button" onClick={() => onRemove(d.r2_key)} className="text-rose-300 hover:text-rose-200 ml-auto shrink-0"><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TCScroll({ onReadComplete }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
        onReadComplete();
      }
    };
    el.addEventListener("scroll", check, { passive: true });
    check(); // in case content is short enough to fit
    return () => el.removeEventListener("scroll", check);
  }, [onReadComplete]);
  return (
    <div ref={ref} className="max-h-[60vh] overflow-y-auto pr-2 space-y-4 text-sm leading-relaxed" data-testid="aa-tc-scroll">
      {HIDROVERSE_TC.map((s, i) => (
        <div key={i}>
          <h3 className="font-display font-bold text-base text-[#1fb6c1] mb-2">{s.heading}</h3>
          <div className="space-y-2">
            {s.body.map((p, j) => <p key={j} className="text-[#cdd3de]">{p}</p>)}
          </div>
        </div>
      ))}
      <div className="text-center text-xs text-emerald-300 py-4 border-t border-white/10">
        <CheckCircle2 className="inline w-4 h-4 mr-1" /> End of terms. You may now accept.
      </div>
    </div>
  );
}

function DraftBar({ token, shareUrl, saving, lastSaved, onSave }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* ignore */ }
  };
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 text-xs" data-testid="aa-draft-bar">
      <button type="button" onClick={onSave} disabled={saving} className="hv-btn hv-btn-ghost text-xs" data-testid="aa-draft-save">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        {saving ? "Saving…" : (token ? "Update draft & get resume link" : "Save draft & get resume link")}
      </button>
      {shareUrl && (
        <div className="flex items-center gap-2 flex-1 min-w-[260px] bg-white/[0.02] border border-white/10 rounded-md px-3 py-1.5">
          <LinkIcon className="w-3.5 h-3.5 text-[#1fb6c1] shrink-0" />
          <input readOnly value={shareUrl} className="bg-transparent text-xs text-[#a0aabf] flex-1 outline-none" data-testid="aa-draft-link" />
          <button type="button" onClick={copy} className="hv-btn hv-btn-ghost px-2 py-1 text-[10px]" data-testid="aa-draft-copy">
            {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-300" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      )}
      {lastSaved && <div className="text-[#647087]">Last saved {new Date(lastSaved).toLocaleString('en-GB')}</div>}
    </div>
  );
}

function SuccessPanel({ done }) {
  return (
    <div className="min-h-screen bg-[#0a0b0e] text-white flex items-center justify-center px-4" data-testid="aa-success">
      <div className="max-w-xl w-full hv-card p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-400/10 border border-emerald-400/40 mx-auto flex items-center justify-center mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-300" />
        </div>
        <h1 className="font-display text-2xl font-bold">Application received</h1>
        <p className="text-sm text-[#a0aabf] mt-3">
          Thank you, <span className="text-white">{done?.contact_name || done?.trading_name}</span>. Your application reference is <span className="font-mono text-[#1fb6c1]">{done?.id?.slice(0, 8)}</span>.
          We'll email you within 3–5 business days with a credit decision.
        </p>
        <div className="mt-6 text-xs text-[#647087]">
          <Sparkles className="inline w-3.5 h-3.5 mr-1 text-[#1fb6c1]" />
          Need to contact us meanwhile? <a href="mailto:billing@hidroverse.co.za" className="text-[#1fb6c1] underline">billing@hidroverse.co.za</a>
        </div>
      </div>
    </div>
  );
}
